import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MapPin } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/components/feedback/EmptyState";
import { listCustomers } from "@/services/api/customers";
import { listLoads } from "@/services/api/loads";
import { getMapsKey } from "@/services/api/settings";
import { listSites } from "@/services/api/sites";
import type { Customer, Load, Site } from "@/services/api/types";

/**
 * /admin/route-planner — date-ranged load map.
 *
 * Plots every load in the selected window on a Google Map (right panel), keyed
 * by customer address. Each day's loads share a border colour (legend on the
 * left). Customers that are linked/associated on the customer page are grouped
 * and their loads are stacked above each other on the map.
 */

const DAY_COLORS = [
  "#e11d48", // rose
  "#2563eb", // blue
  "#16a34a", // green
  "#d97706", // amber
  "#7c3aed", // violet
  "#0891b2", // cyan
  "#db2777", // pink
  "#65a30d", // lime
];

type GoogleMaps = {
  maps: {
    Map: new (el: HTMLElement, opts: unknown) => unknown;
    LatLngBounds: new () => {
      extend: (point: { lat: number; lng: number }) => void;
      getCenter: () => { lat: number; lng: number };
      isEmpty: () => boolean;
    };
    Geocoder: new () => {
      geocode: (
        request: { address: string },
        cb: (results: Array<{ geometry: { location: { lat(): number; lng(): number } } }>, status: string) => void,
      ) => void;
    };
    Marker: new (opts: {
      position: { lat: number; lng: number };
      map: unknown;
      title?: string;
      icon?: unknown;
    }) => unknown;
    InfoWindow: new (opts?: { content?: string }) => {
      setContent: (content: string) => void;
      open: (opts: { map?: unknown; anchor?: unknown }) => void;
      close: () => void;
    };
    event: {
      addListener: (target: unknown, eventName: string, handler: () => void) => unknown;
      trigger: (target: unknown, eventName: string) => void;
    };
    SymbolPath: { CIRCLE: number; SQUARE: number };
  };
};

/* ------------------------------------------------------------------ dates */

function todayIso(): string {
  const date = new Date();
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function toIso(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(iso: string, delta: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + delta);
  return toIso(d);
}

/** The first `count` weekdays (Mon–Fri) after (and excluding) `iso`. */
function addWorkdays(iso: string, count: number): string {
  let current = iso;
  let added = 0;
  while (added < count) {
    current = addDays(current, 1);
    const weekday = new Date(`${current}T00:00:00`).getDay();
    if (weekday !== 0 && weekday !== 6) added += 1;
  }
  return current;
}

function dateRange(from: string, to: string): string[] {
  const days: string[] = [];
  let current = from;
  let guard = 0;
  while (current <= to && guard < 370) {
    days.push(current);
    current = addDays(current, 1);
    guard += 1;
  }
  return days;
}

function formatDay(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/* --------------------------------------------------- linked-customer groups */

/** Connected components of the customer link graph → group index per customer. */
function linkGroups(customers: Customer[]): Map<number, number> {
  const adj = new Map<number, number[]>();
  for (const customer of customers) {
    adj.set(customer.id, [...(customer.linked_customer_ids ?? [])]);
  }
  for (const [id, links] of adj) {
    for (const link of links) {
      if (!adj.has(link)) adj.set(link, []);
      const targets = adj.get(link)!;
      if (!targets.includes(id)) targets.push(id);
    }
  }
  const group = new Map<number, number>();
  let groupIndex = 0;
  for (const id of adj.keys()) {
    if (group.has(id)) continue;
    const stack = [id];
    while (stack.length) {
      const current = stack.pop()!;
      if (group.has(current)) continue;
      group.set(current, groupIndex);
      for (const neighbour of adj.get(current) ?? []) {
        if (!group.has(neighbour)) stack.push(neighbour);
      }
    }
    groupIndex += 1;
  }
  return group;
}

/* ---------------------------------------------------- address helpers */

/** Escape HTML in values interpolated into the marker info card. */
function esc(text: string): string {
  return text.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}

/** A geocodable address for a load: customer address+postcode, else the load's
 * own destination address+postcode (already saved / imported). */
function addressFor(load: Load, customersById: Map<number, Customer>): string | null {
  const customer = customersById.get(load.customer_id);
  const parts: string[] = [];
  const address = customer?.address || load.destination_address;
  const postcode = customer?.postcode || load.postcode;
  if (address) parts.push(address.trim());
  if (postcode) parts.push(postcode.trim());
  return parts.length > 0 ? parts.join(", ") : null;
}

/** HTML for the marker info card (load details). */
function cardHtml(load: Load, customer: Customer | undefined, address: string): string {
  const rows: Array<[string, string]> = [
    ["Load", load.load_ref],
    ["Customer", customer?.name ?? `Customer ${load.customer_id}`],
    ["Direction", load.direction],
    ["Delivery", load.delivery_date ?? "—"],
    ["Pallets", load.total_pallet_count?.toString() ?? "—"],
    ["Address", address],
  ];
  const body = rows
    .map(
      ([key, value]) =>
        `<div style="display:flex;justify-content:space-between;gap:14px;padding:2px 0;">` +
        `<span style="color:var(--color-muted-foreground);">${esc(key)}</span>` +
        `<span style="font-weight:600;color:var(--color-card-foreground);">${esc(value)}</span></div>`,
    )
    .join("");
  // Background/foreground use the app theme tokens so the card stays readable
  // in both light and dark themes (dark card + light text when the theme is
  // dark — the map card background follows the site theme).
  return (
    `<div style="background:var(--color-muted);color:var(--color-card-foreground);border:1px solid var(--color-border);border-radius:10px;padding:10px 12px;font-family:ui-sans-serif,system-ui,sans-serif;font-size:13px;min-width:230px;box-shadow:0 6px 16px rgba(0,0,0,0.18);">` +
    `<div style="font-weight:700;margin-bottom:6px;padding-bottom:6px;border-bottom:1px solid var(--color-border);color:var(--color-card-foreground);">${esc(load.load_ref)}</div>` +
    body +
    `</div>`
  );
}

/** A geocodable address for a site (address + postcode). */
function siteAddressFor(site: Site): string | null {
  const parts = [site.address, site.postcode].filter(Boolean) as string[];
  return parts.length > 0 ? parts.join(", ") : null;
}

/** HTML for a site marker's info card (distinct from the load cards). */
function siteCardHtml(site: Site): string {
  const rows: Array<[string, string]> = [
    ["Code", site.site_code],
    ["Name", site.name],
    ["Address", siteAddressFor(site) ?? "—"],
    ["Type", site.is_depot ? "Depot" : "Site"],
    ["Bays", String(site.bays_count)],
  ];
  const body = rows
    .map(
      ([key, value]) =>
        `<div style="display:flex;justify-content:space-between;gap:14px;padding:2px 0;">` +
        `<span style="color:var(--color-muted-foreground);">${esc(key)}</span>` +
        `<span style="font-weight:600;color:var(--color-card-foreground);">${esc(value)}</span></div>`,
    )
    .join("");
  return (
    `<div style="background:var(--color-muted);color:var(--color-card-foreground);border:1px solid var(--color-border);border-radius:10px;padding:10px 12px;font-family:ui-sans-serif,system-ui,sans-serif;font-size:13px;min-width:230px;box-shadow:0 6px 16px rgba(0,0,0,0.18);">` +
    `<div style="font-weight:700;margin-bottom:6px;padding-bottom:6px;border-bottom:1px solid var(--color-border);">${esc(site.site_code)}</div>` +
    body +
    `</div>`
  );
}

/* ------------------------------------------------------------- map panel */

interface RouteMapProps {
  apiKey: string;
  loads: Load[];
  sites: Site[];
  customersById: Map<number, Customer>;
  groups: Map<number, number>;
  colorByDay: Map<string, string>;
}

function RouteMap({ apiKey, loads, sites, customersById, groups, colorByDay }: RouteMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<unknown | null>(null);
  const markersRef = useRef<unknown[]>([]);
  const infoWindowRef = useRef<{
    setContent: (content: string) => void;
    open: (opts: { map?: unknown; anchor?: unknown }) => void;
    close: () => void;
  } | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [plotInfo, setPlotInfo] = useState<string | null>(null);

  // Load the Google Maps JS API once the key is known.
  useEffect(() => {
    if (!apiKey) {
      setStatus("error");
      return;
    }
    const win = window as unknown as { google?: GoogleMaps; __gmapsReady?: () => void };
    if (win.google?.maps) {
      setStatus("ready");
      return;
    }
    const existing = document.getElementById("gmaps-script");
    if (existing) {
      const onLoad = () => setStatus("ready");
      existing.addEventListener("load", onLoad);
      return () => existing.removeEventListener("load", onLoad);
    }
    setStatus("loading");
    win.__gmapsReady = () => setStatus("ready");
    const script = document.createElement("script");
    script.id = "gmaps-script";
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=__gmapsReady&loading=async`;
    script.onerror = () => setStatus("error");
    document.head.appendChild(script);
    return () => {
      script.onerror = null;
    };
  }, [apiKey]);

  // Initialise the map once the API is ready, and keep it filling its container
  // (the right column grows/shrinks with the load list).
  useEffect(() => {
    if (status !== "ready" || !containerRef.current || mapRef.current) return;
    const g = (window as unknown as { google: GoogleMaps }).google;
    const container = containerRef.current;
    mapRef.current = new g.maps.Map(container, {
      center: { lat: 53.2, lng: -1.5 },
      zoom: 6,
    });
    const observer = new ResizeObserver(() => {
      g.maps.event.trigger(mapRef.current, "resize");
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [status]);

  // Plot the loads + sites. Uses SAVED latitude/longitude when present (set by
  // the backend when the address is validated) so we don't re-geocode on every
  // search; only addresses without saved coords are geocoded here.
  useEffect(() => {
    if (status !== "ready" || !mapRef.current) return;
    const g = (window as unknown as { google: GoogleMaps }).google;

    // Clear markers from a previous plot (e.g. after toggling a day).
    for (const marker of markersRef.current) {
      (marker as { setMap: (map: unknown) => void }).setMap(null);
    }
    markersRef.current = [];
    if (infoWindowRef.current) infoWindowRef.current.close();
    infoWindowRef.current = new g.maps.InfoWindow();
    setPlotInfo(null);

    const geocoder = new g.maps.Geocoder();

    // customer id -> coordinates. Saved coords win; the rest go to geocoding.
    const resolvedByCustomer = new Map<number, { lat: number; lng: number }>();
    const toGeocode = new Map<string, number[]>(); // address -> customer ids

    for (const load of loads) {
      const customer = customersById.get(load.customer_id);
      if (!customer) continue;
      if (customer.latitude != null && customer.longitude != null) {
        resolvedByCustomer.set(customer.id, {
          lat: customer.latitude,
          lng: customer.longitude,
        });
      } else {
        const address = addressFor(load, customersById);
        if (address) {
          const ids = toGeocode.get(address) ?? [];
          ids.push(customer.id);
          toGeocode.set(address, ids);
        }
      }
    }

    // site id -> coordinates (distinct square pins). Same saved-first logic.
    const resolvedSites = new Map<number, { lat: number; lng: number }>();
    const sitesToGeocode = new Map<string, number[]>(); // address -> site ids

    for (const site of sites) {
      if (site.latitude != null && site.longitude != null) {
        resolvedSites.set(site.id, { lat: site.latitude, lng: site.longitude });
      } else {
        const address = siteAddressFor(site);
        if (address) {
          const ids = sitesToGeocode.get(address) ?? [];
          ids.push(site.id);
          sitesToGeocode.set(address, ids);
        }
      }
    }

    let pending = toGeocode.size + sitesToGeocode.size;
    let geocoded = 0;
    let geocodeFailed = 0;

    const placeMarkers = () => {
      const markerOffset = new Map<number, number>();
      let placedLoads = 0;
      let placedSites = 0;
      const bounds = new g.maps.LatLngBounds();

      // Loads: day-coloured circles (linked customers stacked).
      for (const load of loads) {
        const customer = customersById.get(load.customer_id);
        const coord = customer ? resolvedByCustomer.get(customer.id) : undefined;
        if (!coord) continue;
        const day = load.delivery_date ?? "";
        const colour = colorByDay.get(day) ?? "#64748b";
        const groupIndex = groups.get(customer!.id) ?? -1;
        const memberIndex = markerOffset.get(groupIndex) ?? 0;
        markerOffset.set(groupIndex, memberIndex + 1);
        // Stack linked customers' loads vertically above each other.
        const offset = groupIndex >= 0 ? memberIndex * 0.006 : 0;
        const position = { lat: coord.lat + offset, lng: coord.lng + offset };
        const marker = new g.maps.Marker({
          position,
          map: mapRef.current,
          title: `${customer!.name} — ${load.load_ref}`,
          icon: {
            path: g.maps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: colour,
            fillOpacity: 1,
            strokeColor: "#0f172a",
            strokeWeight: 2,
          },
        });
        markersRef.current.push(marker);
        placedLoads += 1;
        bounds.extend(position);
        const address = addressFor(load, customersById) ?? "";
        g.maps.event.addListener(marker, "click", () => {
          infoWindowRef.current?.setContent(cardHtml(load, customer, address));
          infoWindowRef.current?.open({ map: mapRef.current, anchor: marker });
        });
      }

      // Sites: distinct SQUARE pins so they stand out from the load circles.
      for (const site of sites) {
        const coord = resolvedSites.get(site.id);
        if (!coord) continue;
        const marker = new g.maps.Marker({
          position: coord,
          map: mapRef.current,
          title: `${site.site_code} — ${site.name}`,
          icon: {
            path: g.maps.SymbolPath.SQUARE,
            scale: 7,
            fillColor: site.color || (site.is_depot ? "#0f766e" : "#7c3aed"),
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
          },
        });
        markersRef.current.push(marker);
        placedSites += 1;
        bounds.extend(coord);
        g.maps.event.addListener(marker, "click", () => {
          infoWindowRef.current?.setContent(siteCardHtml(site));
          infoWindowRef.current?.open({ map: mapRef.current, anchor: marker });
        });
      }

      // Zoom to fit every pin (loads + sites) so all are visible.
      const totalPlaced = placedLoads + placedSites;
      if (totalPlaced > 0) {
        const map = mapRef.current as {
          fitBounds: (bounds: unknown, padding?: number) => void;
          setCenter: (center: { lat: number; lng: number }) => void;
          setZoom: (zoom: number) => void;
        };
        if (totalPlaced === 1) {
          // A single pin: centre on it with a sensible zoom (fitBounds would
          // zoom to the maximum for one point).
          map.setCenter(bounds.getCenter());
          map.setZoom(11);
        } else {
          map.fitBounds(bounds, 60);
        }
      }

      const savedCount = resolvedByCustomer.size + resolvedSites.size - geocoded;
      setPlotInfo(
        totalPlaced === 0
          ? pending > 0
            ? "No pins could be placed — enable the Google Geocoding API for the Maps API key, then re-save addresses."
            : "No loads or sites with geocodable addresses in this window."
          : `${totalPlaced} pin${totalPlaced === 1 ? "" : "s"} (${placedLoads} load${placedLoads === 1 ? "" : "s"}, ${placedSites} site${placedSites === 1 ? "" : "s"}; ${savedCount} saved coords, ${geocoded} geocoded${geocodeFailed > 0 ? `, ${geocodeFailed} failed` : ""}).`,
      );
    };

    toGeocode.forEach((ids, address) => {
      geocoder.geocode({ address }, (results, statusCode) => {
        pending -= 1;
        if (statusCode === "OK" && results[0]?.geometry?.location) {
          const location = results[0].geometry.location;
          const coord = { lat: location.lat(), lng: location.lng() };
          for (const id of ids) resolvedByCustomer.set(id, coord);
          geocoded += 1;
        } else {
          geocodeFailed += 1;
        }
        if (pending === 0) placeMarkers();
      });
    });
    sitesToGeocode.forEach((ids, address) => {
      geocoder.geocode({ address }, (results, statusCode) => {
        pending -= 1;
        if (statusCode === "OK" && results[0]?.geometry?.location) {
          const location = results[0].geometry.location;
          const coord = { lat: location.lat(), lng: location.lng() };
          for (const id of ids) resolvedSites.set(id, coord);
          geocoded += 1;
        } else {
          geocodeFailed += 1;
        }
        if (pending === 0) placeMarkers();
      });
    });
    if (pending === 0) placeMarkers();
  }, [status, loads, sites, customersById, groups, colorByDay]);

  return (
    <div className="relative h-full min-h-[560px] overflow-hidden rounded-xl border bg-muted/20">
      <div ref={containerRef} className="h-full w-full" />
      {status === "loading" && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60 text-sm text-muted-foreground">
          Loading Google Maps…
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex items-center justify-center p-6">
          <EmptyState
            title="Map unavailable"
            description="Add a Google Maps API key on the Settings page to plot the route planner."
          />
        </div>
      )}
      {status === "ready" && plotInfo && (
        <div className="absolute inset-x-2 bottom-2 z-10 rounded-md bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow">
          {plotInfo}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- the page */

export function RoutePlannerPage() {
  const [start, setStart] = useState(todayIso());
  const [end, setEnd] = useState(() => addWorkdays(todayIso(), 3));
  // Days whose loads are hidden from the map (checkbox per day, ticked by
  // default so all days' loads show).
  const [hiddenDays, setHiddenDays] = useState<Set<string>>(() => new Set());

  const days = useMemo(() => dateRange(start, end), [start, end]);
  const colorByDay = useMemo(() => {
    const map = new Map<string, string>();
    days.forEach((day, index) => map.set(day, DAY_COLORS[index % DAY_COLORS.length]));
    return map;
  }, [days]);

  const loads = useQuery({
    queryKey: ["route-planner", start, end],
    queryFn: () => listLoads({ limit: 1000, date_from: start, date_to: end }),
  });
  const customers = useQuery({
    queryKey: ["customers", "all"],
    queryFn: () => listCustomers({ limit: 500 }),
  });
  const sites = useQuery({
    queryKey: ["sites", "all"],
    queryFn: () => listSites({ limit: 500 }),
  });
  const mapsKey = useQuery({
    queryKey: ["maps-key"],
    queryFn: () => getMapsKey(),
  });

  const customersById = useMemo(() => {
    const map = new Map<number, Customer>();
    for (const customer of customers.data?.items ?? []) map.set(customer.id, customer);
    return map;
  }, [customers.data]);

  const groups = useMemo(() => linkGroups(customers.data?.items ?? []), [customers.data]);

  const loadsByDay = useMemo(() => {
    const map = new Map<string, Load[]>();
    for (const load of loads.data?.items ?? []) {
      const day = load.delivery_date ?? "";
      if (!day) continue;
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(load);
    }
    return map;
  }, [loads.data]);

  // Loads passed to the map — filtered by the per-day visibility checkboxes.
  const visibleLoads = useMemo(
    () =>
      (loads.data?.items ?? []).filter((load) => !hiddenDays.has(load.delivery_date ?? "")),
    [loads.data, hiddenDays],
  );

  const toggleDay = (day: string, visible: boolean) => {
    setHiddenDays((prev) => {
      const next = new Set(prev);
      if (visible) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">Route Planner
          </h1>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="rp-start">Start date</Label>
            <Input
              id="rp-start"
              type="date"
              value={start}
              onChange={(event) => setStart(event.target.value || todayIso())}
              className="mt-1 w-44"
            />
          </div>
          <div>
            <Label htmlFor="rp-end">End date</Label>
            <Input
              id="rp-end"
              type="date"
              value={end}
              onChange={(event) => setEnd(event.target.value || start)}
              className="mt-1 w-44"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setStart(todayIso());
              setEnd(addWorkdays(todayIso(), 3));
            }}
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Day colour legend */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border p-3">
        <MapPin className="h-4 w-4 text-muted-foreground" />
        {days.map((day) => (
          <Badge
            key={day}
            variant="outline"
            style={{ borderColor: colorByDay.get(day), color: colorByDay.get(day) }}
          >
            {formatDay(day)}
          </Badge>
        ))}
        {days.length === 0 && (
          <span className="text-sm text-muted-foreground">No days selected</span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Left: grouped load list */}
        <div className="space-y-4">
          {days.map((day) => {
            const dayLoads = loadsByDay.get(day) ?? [];
            const colour = colorByDay.get(day) ?? "#64748b";
            return (
              <div key={day} className="rounded-lg border bg-card p-3">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={!hiddenDays.has(day)}
                      onCheckedChange={(checked) => toggleDay(day, checked !== false)}
                      aria-label={`Show loads for ${formatDay(day)} on the map`}
                    />
                    <span
                      className="rounded px-2 py-0.5 text-sm font-medium text-white"
                      style={{ backgroundColor: colour }}
                    >
                      {formatDay(day)}
                    </span>
                  </label>
                  <span className="text-xs text-muted-foreground">{dayLoads.length} loads</span>
                </div>
                {dayLoads.length === 0 ? (
                  <p className="py-3 text-sm text-muted-foreground">No loads this day.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {dayLoads.map((load) => {
                      const customer = customersById.get(load.customer_id);
                      return (
                        <li
                          key={load.id}
                          className="flex items-center justify-between gap-2 rounded border-l-4 bg-muted/40 px-3 py-1.5 text-sm"
                          style={{ borderLeftColor: colour }}
                        >
                          <span className="font-medium">{load.load_ref}</span>
                          <span className="truncate text-muted-foreground">
                            {customer?.name ?? `Customer ${load.customer_id}`}
                          </span>
                          <Badge variant="secondary" className="shrink-0">
                            {load.direction}
                          </Badge>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        {/* Right: map */}
        <RouteMap
          apiKey={mapsKey.data?.api_key ?? ""}
          loads={visibleLoads}
          sites={sites.data?.items ?? []}
          customersById={customersById}
          groups={groups}
          colorByDay={colorByDay}
        />
      </div>
    </div>
  );
}
